import React, { useState, useEffect, useContext } from "react";
import { Table, Radio, InputNumber, Space, Typography } from "antd";
import { DataContext } from './DataContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';

const { Title } = Typography;

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

const MasterAmortizationTable = ({ usedSpace, totalSpace, isUserAllowed }) => {

  const [initialValue, setInitialValue] = useState(0);
  const [increaseType1, setIncreaseType1] = useState("10");
  const [customRate1, setCustomRate1] = useState(0);

  const [increaseType2, setIncreaseType2] = useState("10");
  const [customRate2, setCustomRate2] = useState(0);
  const { setSharedData } = useContext(DataContext);

  const getRate = (type, custom) => {
    return type === "custom" ? Number(custom) : Number(type);
  };

  useEffect(() => {
    setSharedData({
      initialValue,
      increaseType1,
      customRate1,
      increaseType2,
      customRate2,
      rate1: getRate(increaseType1, customRate1),
      rate2: getRate(increaseType2, customRate2)
    });
  }, [initialValue, increaseType1, customRate1, increaseType2, customRate2, setSharedData]);

  const calculateBackupData = () => {
    const rate = getRate(increaseType1, customRate1);
    let value = Number(initialValue);
    return years.map((year, index) => {
      if (index !== 0) value += value * (rate / 100);
      return {
        key: year,
        year,
        cost: Number(value.toFixed(2)),
      };
    });
  };

  const calculateCurrentUsageData = () => {
    const rate = getRate(increaseType2, customRate2);
    let value = Number(usedSpace);
    return years.map((year, index) => {
      if (index !== 0) value += value * (rate / 100);
      return {
        key: year,
        year,
        cost: Number(value.toFixed(2)),
      };
    });
  };

  const backupData = calculateBackupData();
  const currentUsedData = calculateCurrentUsageData();

  const calculateTotalData = () => {
    return years.map((year, index) => {
      const costSum = backupData[index].cost + currentUsedData[index].cost;
      return {
        key: year,
        year,
        totalCost: Number(costSum.toFixed(2)),
        exceed: costSum > (totalSpace * 0.8),
        additionalStorage: backupData[index].cost,
        usedStorage: currentUsedData[index].cost,
      };
    });
  };

  const TotalData = calculateTotalData();

  const BackupTableColumns = [
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      align: "center",
    },
    {
      title: "Storage Cost",
      dataIndex: "cost",
      key: "cost",
      align: "center",
    },
  ];

  const CurrentUsageTableColumns = [
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      align: "center",
    },
    {
      title: "Used Space",
      dataIndex: "cost",
      key: "cost",
      align: "center",
    },
  ];

  const TotalDataTableColumns = [
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      align: "center",
    },
    {
      title: "Total Usage",
      dataIndex: "totalCost",
      key: "totalCost",
      align: "center",
      render: (text, record) => (
        <span style={{ color: record.exceed ? "red" : "inherit" }}>
          {text}
        </span>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Title level={4}>Storage Cost Amortization ({currentYear}–{currentYear + 4})</Title>

      <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", marginBottom: 24 }}>
        {/* First Table */}
        <div style={{ flex: 1 }}>
          <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
            {/* <span>Initial Value for Backup Storage :</span> */}
            <span>Additional Backup Storage :</span>
            <InputNumber
              min={0}
              value={initialValue}
              onChange={setInitialValue}
              style={{ width: "100%" }}
              disabled={!isUserAllowed}
            />
            <span>Increase Rate for Additional ROBO Storage:</span>
            <Radio.Group
              value={increaseType1}
              onChange={(e) => setIncreaseType1(e.target.value)}
              disabled={!isUserAllowed}
            >
              <Radio value="5">5%</Radio>
              <Radio value="10">10%</Radio>
              <Radio value="15">15%</Radio>
              <Radio value="custom">Custom</Radio>
            </Radio.Group>
            {increaseType1 === "custom" && (
              <InputNumber
                min={0}
                placeholder="Custom %"
                value={customRate1}
                onChange={setCustomRate1}
                style={{ width: "100%" }}
                disabled={!isUserAllowed}
              />
            )}
          </Space>

          <Table
            bordered
            columns={BackupTableColumns}
            dataSource={backupData}
            pagination={false}
            style={{ backgroundColor: "#f5f5f5" }}
            title={() => "Additional ROBO Storage Growth"}
          />
        </div>

        {/* Second Table */}
        <div style={{ flex: 1, marginTop: 65 }}>
          <Space direction="vertical" style={{ width: "100%", marginBottom: 20 }}>
            <span>Increase Rate for Used Space (Initial: {usedSpace} TB):</span>
            <Radio.Group
              value={increaseType2}
              onChange={(e) => setIncreaseType2(e.target.value)}
              disabled={!isUserAllowed}
            >
              <Radio value="5">5%</Radio>
              <Radio value="10">10%</Radio>
              <Radio value="15">15%</Radio>
              <Radio value="custom">Custom</Radio>
            </Radio.Group>
            {increaseType2 === "custom" && (
              <InputNumber
                min={0}
                placeholder="Custom %"
                value={customRate2}
                onChange={setCustomRate2}
                style={{ width: "100%" }}
                disabled={!isUserAllowed}
              />
            )}
          </Space>

          <Table
            bordered
            columns={CurrentUsageTableColumns}
            dataSource={currentUsedData}
            pagination={false}
            style={{ backgroundColor: "#f5f5f5" }}
            title={() => "Used Space Growth"}
          />
        </div>

        {/* Third Table */}
        <div style={{ flex: 1, marginTop: 135 }}>
          <Table
            bordered
            columns={TotalDataTableColumns}
            dataSource={TotalData}
            pagination={false}
            style={{ backgroundColor: "#f5f5f5" }}
            title={() => `Total Usage Limit (80% of Total Capacity: ${(totalSpace * 0.8).toFixed(2)} TB)`}
          />
        </div>
      </div>
      <div style={{ marginTop: 30 }}>
        <Title level={4}>Commvault Projection and Capacity Graph</Title>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={TotalData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis label={{ value: 'Storage (TB)', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => `${value} TB`} />
            <Legend />
            <ReferenceLine
              y={totalSpace}
              label={{
                value: `Total Capacity: ${totalSpace} TB`,
                position: 'insideTopLeft',
                fill: 'red',
                fontWeight: 'bold',
                offset: 15,
              }}
              stroke="red"
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <ReferenceLine
              y={totalSpace * 0.8}
              label={{
                value: `80% Threshold: ${Number(totalSpace * 0.8).toFixed(2)} TB`,
                position: 'insideTopLeft',
                fill: 'magenta',
                fontWeight: 'bold',
                offset: 15,
              }}
              stroke="magenta"
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <Line type="monotone" dataKey="additionalStorage" name="Additional ROBO Storage" stroke="#8884d8" activeDot={{ r: 8 }} strokeWidth={2} />
            <Line type="monotone" dataKey="usedStorage" name="Used Storage" stroke="#82ca9d" activeDot={{ r: 8 }} strokeWidth={2} />
            <Line type="monotone" dataKey="totalCost" name="Total Usage" stroke="#ff7300" activeDot={{ r: 8 }} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default MasterAmortizationTable;